import React, { useCallback, useState } from 'react';
import { browserHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'reducers/index';

// Components
import CardsModalRoot from 'cards/components/common/CardsModalRoot';
import { APSamplePreviewModule } from 'cards/components/common/Samples/SingleSampleAccessPoint';

// Actions
import { customizeCard } from 'cards/reducers/pdp/actions';
import { getUserContext as getUserContextAction } from 'actions/UserActions';
import { hideModal, openModal } from 'cards/actions/modalActions';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';

// Selectors
import { getActiveSelection, getActiveMediumKey } from 'cards/reducers/pdp/selectors';
import getCustomizationTrackedEvents from 'cards/selectors/tracking';
import { getIsAdminView } from 'cards/selectors/cardCatalogSelector';
import { getWeddingThemeKey } from 'selectors/website/websiteSelectors';
import { getUserContext as getUserContextSelector } from 'selectors/user/userSelectors';

// Hooks
import { useMostPopularSamplesFromFamily } from 'cards/components/common/Samples/util/hooks';
import { useRelatedDraftsV2 } from 'cards/util/existing-drafts-hook';

// Types
import type { APSamplePreviewModuleProps } from 'cards/components/common/Samples/SingleSampleAccessPoint/SingleSampleAccessPointUI/APSamplePreviewModule/APSamplePreviewModule';
import { CARD_TYPES } from 'cards/constants/CardTypes';
import { ToastOptions } from '@zola-helpers/client/dist/es/redux/toasts/toastActionTypes';

// Utils
import { createInvitationsAccount } from 'paper/utils/account';
import { trackCardCustomizationStarted } from 'paper/utils/trackingEvents';

// Styles
import { Container } from './PaperCustomizationCrossSell.styles';

interface PaperCustomizationCrossSellProps
  extends Pick<
    APSamplePreviewModuleProps,
    | 'backgroundColor'
    | 'className'
    | 'ctaTextOverride'
    | 'ctaType'
    | 'disableResponsiveStyling'
    | 'enableColorPicker'
    | 'enableFrame'
    | 'hed'
    | 'showQrCodeBubble'
    | 'subtitleOverride'
    | 'trackingParams'
  > {
  cardType?: string;
}

const PaperCustomizationCrossSell = ({
  backgroundColor,
  cardType = CARD_TYPES.dstd,
  className,
  ctaTextOverride,
  ctaType,
  disableResponsiveStyling,
  enableColorPicker,
  enableFrame,
  hed,
  showQrCodeBubble,
  subtitleOverride,
  trackingParams,
}: PaperCustomizationCrossSellProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const customizationTrackedEvents = useSelector(getCustomizationTrackedEvents);
  const themeKey = useSelector(state => getWeddingThemeKey(state));
  const userContext = useSelector(getUserContextSelector);
  const errorToast = useCallback((opts: ToastOptions) => dispatch(toastsActions.negative(opts)), [
    dispatch,
  ]);
  const activeMediumKey = useSelector(getActiveMediumKey);
  const activeSelection = useSelector(getActiveSelection);
  const isAdminView = useSelector(getIsAdminView);
  const [activeColor, setActiveColor] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const { cardInfo, fetchedNextMostPopular } = useMostPopularSamplesFromFamily(
    themeKey,
    cardType,
    suites => suites[0],
    true
  );
  const relatedDrafts = useRelatedDraftsV2(cardInfo?.uuid);
  const isDstd = cardType === CARD_TYPES.dstd;
  if (!cardInfo) return <></>;

  const showErrorToast = () => {
    setIsCreatingProject(false);
    errorToast({ headline: 'Something went wrong. Please try again.' });
  };

  const updateActiveColor = (color: string) => {
    setActiveColor(color);
  };

  const createProject = () => {
    const leadCard = cardInfo.sampleCard?.lead_card;
    const index =
      isDstd && activeColor ? leadCard?.colors?.findIndex(c => c.value === activeColor) || 0 : 0;
    const activeVariationUuid = leadCard?.colors?.[index]?.default_variation_uuid;
    const quanity = !isDstd && (leadCard?.foil || leadCard?.letterpress) ? 100 : 1;

    dispatch(customizeCard(cardInfo.uuid, activeVariationUuid, quanity, isAdminView, isDstd))
      .then((result: { uuid: string }) => {
        const { uuid: projectUuid } = result;
        trackCardCustomizationStarted(
          cardType,
          projectUuid,
          customizationTrackedEvents[projectUuid],
          activeMediumKey,
          activeSelection.size
        );
        setIsCreatingProject(false);

        // Needed because we are skipping redirect for dstd when dispatching customizeCard
        if (isDstd) {
          browserHistory.push(`/wedding-planning/custom-digital/${projectUuid}/front`);
        }
      })
      .catch(() => {
        showErrorToast();
      });
  };

  const startCustomization = () => {
    if (isCreatingProject) return;
    setIsCreatingProject(true);

    if (
      userContext.has_invitation_account &&
      userContext.invitation_account?.cards_onboarding_completed
    ) {
      if (!relatedDrafts) {
        showErrorToast();
        return;
      }

      if (relatedDrafts.length > 0) {
        dispatch(
          openModal(
            'EXISTING_DRAFTS_FOR_CARD_SUITE',
            {
              relatedDrafts,
              onStartNewProject: () => {
                dispatch(hideModal());
                createProject();
              },
              isDstd,
            },
            {
              size: 'sm',
              bottomModalOnMobile: true,
              closeCallback: () => setIsCreatingProject(false),
            }
          )
        );
      } else {
        createProject();
      }
    } else {
      createInvitationsAccount()
        .then(async accountCreationResult => {
          if (accountCreationResult.errors) {
            throw new Error('There was an error creating an invitations account.');
          }

          await dispatch(getUserContextAction());
          createProject();
        })
        .catch(() => {
          showErrorToast();
        });
    }
  };

  const handleOpenModal = () => {
    // If user hasn't selected website theme or theme doesn't have matching suite, go to PLP; otherwise, start customization
    if (fetchedNextMostPopular) {
      let pathname = '/wedding-planning/digital/save-the-date/shop';
      switch (cardType) {
        case CARD_TYPES.std:
          pathname = '/wedding-planning/save-the-date/shop';
          break;
        case CARD_TYPES.invitation:
          pathname = '/wedding-planning/invitations/shop';
          break;
        default:
      }
      window.location.assign(pathname);
      return;
    }
    startCustomization();
  };

  return (
    <Container>
      <APSamplePreviewModule
        backgroundColor={backgroundColor}
        className={className}
        ctaDisabled={isCreatingProject}
        ctaType={ctaType}
        ctaTextOverride={
          ctaTextOverride || `${fetchedNextMostPopular ? 'Choose' : 'Customize'} your design`
        }
        cardSuiteInfo={cardInfo}
        disableResponsiveStyling={disableResponsiveStyling}
        enableColorPicker={enableColorPicker}
        enableFrame={enableFrame}
        handleOpenModal={handleOpenModal}
        hed={hed}
        showQrCodeBubble={showQrCodeBubble}
        subtitleOverride={subtitleOverride}
        trackingParams={trackingParams}
        updateActiveColor={updateActiveColor}
      />
      <CardsModalRoot />
    </Container>
  );
};

export default PaperCustomizationCrossSell;
